import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@material-ui/core';
import { People, Event, Assessment, Email, Security, Schedule, ChevronRight } from '@material-ui/icons';
import { useHistory } from 'react-router';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

interface TrialUpgradeDialogProps {
  open: boolean;
  onClose: () => void;
  remainingDays: number;
}

export const TrialUpgradeDialog: React.FC<TrialUpgradeDialogProps> = ({ open, onClose, remainingDays }) => {
  const history = useHistory();
  const { theme } = useCampfireTheme();

  const benefits = [
    {
      icon: <People color='primary' />,
      title: 'Custom User Profiles',
      description: 'Collect only the information you need from your volunteers with flexible task builder',
    },
    {
      icon: <Schedule color='primary' />,
      title: 'Flexible Rostering',
      description: 'Schedule events on once-off, ad-hoc or recurring basis with automated notifications',
    },
    {
      icon: <Email color='primary' />,
      title: 'Segmented Communications',
      description: 'Send targeted emails and 2-way SMS to specific volunteer teams with automated reminders',
    },
    {
      icon: <Assessment color='primary' />,
      title: 'Impact Tracking & Reporting',
      description: 'Access impact data and custom reporting to understand volunteers and meet compliance',
    },
    {
      icon: <Security color='primary' />,
      title: 'Role-Based Access Control',
      description: 'Assign roles to users for greater control and build strong safety culture',
    },
    {
      icon: <Event color='primary' />,
      title: 'Self-Service Options',
      description: 'Let volunteers self-organise or manage rosters yourself with full flexibility',
    },
  ];

  const handleUpgrade = () => {
    onClose();
    // Navigate to admin console billing
    history.push('/admin/admin-console/billing');
  };

  const urgencyColour = remainingDays <= 5 ? theme.palette.warning.main : theme.palette.info.main;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
          padding: '8px',
        },
      }}
    >
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Keep your access to Volaby</Typography>
          <Chip
            label={`${remainingDays} ${remainingDays === 1 ? 'day' : 'days'} left`}
            color='primary'
            size='small'
            style={{ backgroundColor: urgencyColour, color: '#fff' }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant='body2' style={{ marginBottom: '16px' }}>
          Unlock the full potential of your volunteer management with these powerful features:
        </Typography>

        <List style={{ padding: 0 }}>
          {benefits.map((benefit, index) => (
            <React.Fragment key={benefit.title}>
              <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: '4px', paddingBottom: '4px' }}>
                <ListItemIcon style={{ minWidth: '32px' }}>
                  {React.cloneElement(benefit.icon, { color: 'inherit' })}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='body2' style={{ fontWeight: 500 }}>
                      {benefit.title}
                    </Typography>
                  }
                  secondary={<Typography variant='caption'>{benefit.description}</Typography>}
                />
              </ListItem>
              {index < benefits.length - 1 && <Divider style={{ margin: '4px 0' }} />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          style={{
            textTransform: 'none',
            color: '#666',
          }}
        >
          Maybe Later
        </Button>
        <Button
          onClick={handleUpgrade}
          variant='contained'
          color='primary'
          endIcon={<ChevronRight />}
          style={{
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            textTransform: 'none',
            borderRadius: '4px',
            padding: '7px 12px',
            fontWeight: 600,
            fontSize: '13px',
          }}
        >
          Choose Your Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};
